// Paket içeriği ağacı (P2: TEK ağaç — "Tüm katalog" görünümü kalktı, ekleme yalnız pencereden).
// Sunucu kalemleri ∪ taslak eklemeleri, KATEGORİ atalarının altında; kategori satırında üç durumlu
// kutu alt ağaçtaki kalemleri toplu çıkarır/geri alır (kategori kendisi asla kalem olmaz). Satır:
// ad · rozetler · birim (soluk) · kod (soluk mono); metne tıklama satırı seçer (ayrıntı paneli),
// onay kutusu çıkarır/geri alır (çıkarılan satır "çıkarılacak" olarak kalır). Kırılım grupları ve
// üyeleri ayrı satır; eksik üyesi olan toplamda amber "kısmi üye". Otomatik eklenen bağımlılık
// nötr "otomatik · X için"; işareti kaldırılır ama hâlâ gerekiyorsa amber "… kayıtta geri eklenir".
// Süzgeç/arama (`filterIds`) uyan kalemleri ve atalarını gösterir.

import { useMemo } from "react";
import type { KpiMetric, KpiPackageItem } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import {
  draftMemberIds,
  effectiveDefaults,
  hasAnyDefault,
  type DraftContext,
  type PackageDraft,
} from "../../lib/kpi-package-draft";
import { categoryBulkState, partialTotalIds, shownItemIds } from "../../lib/kpi-package-editor";
import { useIdsCollapsed } from "../../hooks/use-ids-collapsed";
import { buildIndicatorTree } from "../../lib/kpi-set-editor";
import { CheckTree, type CheckTreeRow } from "./CheckTree";
import { codeStyle, helpText, pillStyle, warningPill } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

const linkStyle: React.CSSProperties = {
  minHeight: 44,
  padding: "0 8px",
  border: 0,
  background: "transparent",
  color: "var(--color-primary-text)",
  fontSize: "0.875rem",
  cursor: "pointer",
  textDecoration: "underline",
};

export interface PackageMembersTreeProps {
  metrics: readonly KpiMetric[];
  ctx: DraftContext;
  base: readonly KpiPackageItem[];
  draft: PackageDraft;
  editable: boolean;
  /** pasif kutu nedeni (salt okunur paket) */
  readOnlyReason: string | null;
  /** süzgeç + arama sonucu; null → tümü */
  filterIds: ReadonlySet<string> | null;
  requiredBy: ReadonlyMap<string, readonly string[]>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** ağacın hemen altında açılan ayrıntı (dar ekran) — sanal listenin DIŞINDA, kaydırma kaymaz */
  detailBelow?: React.ReactNode;
  onToggle: (id: string, on: boolean) => void;
  onToggleCategory: (id: string) => void;
  /** satırı hatalı vurgulanacak göstergeler (kayıt reddi — TODO-KPI-139/1) */
  errorIds?: ReadonlySet<string> | undefined;
  /** süzgeç/arama varken boş sonuçta "Aramayı temizle" */
  onClearFilters?: (() => void) | undefined;
  /** tek kaydırıcı: ağaç kalan yüksekliği doldurur (üst kap yüksekliği tanımlı olmalı) */
  fill?: boolean;
  narrow?: boolean;
}

function Pill(p: {
  tone?: "warn" | "info";
  title?: string | undefined;
  children: string;
}): JSX.Element {
  // DR12 rozet sözlüğü: mavi pill paket üyeliği/varsayılan, amber yalnız uyarı — yeşil yok
  const style = p.tone === "warn" ? warningPill : pillStyle;
  return (
    <span title={p.title} style={style}>
      {p.children}
    </span>
  );
}

export function PackageMembersTree(p: PackageMembersTreeProps): JSX.Element {
  const { ctx, base, draft, editable, filterIds, requiredBy, readOnlyReason } = p;
  const { collapsed, set: setBand, toggleAll } = useIdsCollapsed("paketler");
  const members = useMemo(() => new Set(draftMemberIds(base, draft)), [base, draft]);
  const autoAdded = useMemo(
    () => new Set(base.filter((b) => b.autoAdded).map((b) => b.metricId)),
    [base],
  );
  const baseIds = useMemo(() => new Set(base.map((b) => b.metricId)), [base]);
  const baseById = useMemo(() => new Map(base.map((b) => [b.metricId, b])), [base]);
  const partialTotals = useMemo(() => partialTotalIds(members, ctx), [members, ctx]);
  const metricById = useMemo(() => new Map(p.metrics.map((m) => [m.id, m])), [p.metrics]);
  const shown = useMemo(() => new Set(shownItemIds(base, draft)), [base, draft]);

  const built = useMemo(() => {
    const items = filterIds ? [...shown].filter((id) => filterIds.has(id)) : [...shown];
    const nodes = new Map<string, KpiMetric>();
    for (const id of items) {
      let cur = metricById.get(id);
      const seen = new Set<string>();
      while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        nodes.set(cur.id, cur);
        cur = cur.parentId ? metricById.get(cur.parentId) : undefined;
      }
    }
    const tree = buildIndicatorTree([...nodes.values()]);
    const out: CheckTreeRow[] = [];
    const bandIds: string[] = [];
    const hiddenUnder = new Set<string>();
    for (const r of tree.rows) {
      const id = r.node.id;
      if (r.hasChildren) bandIds.push(id);
      if (r.node.parentId && hiddenUnder.has(r.node.parentId)) {
        hiddenUnder.add(id);
        continue;
      }
      const open = !collapsed.has(id);
      if (r.hasChildren && !open) hiddenUnder.add(id);
      const isItem = shown.has(id) && (!filterIds || filterIds.has(id));
      const row0 = {
        id,
        parentId: r.node.parentId,
        depth: r.depth,
        hasChildren: r.hasChildren,
        expanded: open,
        label: r.node.label,
        ...(p.errorIds?.has(id) ? { error: true } : {}),
      };
      if (!isItem) {
        const category = ctx.categories.has(id);
        out.push({
          ...row0,
          check: category ? categoryBulkState(id, shown, members, ctx, filterIds) : null,
          checkable: category && editable,
          checkOnly: true,
          ...(category && !editable ? { disabledReason: readOnlyReason } : {}),
          weight: r.depth === 0 ? 700 : 600,
          background: r.depth === 0 ? "var(--color-bg)" : undefined,
          meta: <span style={codeStyle}>{r.node.code}</span>,
        });
        continue;
      }
      const on = members.has(id);
      const inBase = baseIds.has(id);
      const req = requiredBy.get(id) ?? [];
      const reqText = req.join(", ");
      const defaults = effectiveDefaults(base, draft, id, baseById);
      const unit = defaults.unitDefault ?? r.node.defaultUnit;
      out.push({
        ...row0,
        check: on ? "all" : "none",
        checkable: editable,
        disabledReason: editable ? null : readOnlyReason,
        selectable: true,
        weight: r.hasChildren ? 600 : 400,
        meta: (
          <>
            {autoAdded.has(id) && on ? (
              <Pill title={reqText ? C.page.dependencyNote(reqText) : undefined}>
                {req.length > 1
                  ? C.pills.autoMore(req[0]!, req.length - 1)
                  : req.length === 1
                    ? C.pills.autoFor(req[0]!)
                    : C.pills.autoBare}
              </Pill>
            ) : null}
            {r.node.status !== "active" ? <Pill tone="warn">{C.pills.deprecated}</Pill> : null}
            {partialTotals.has(id) && on ? <Pill tone="warn">{C.pills.partialMember}</Pill> : null}
            {on && defaults.unitDefault ? (
              <Pill tone="info">{C.pills.defaultUnit(defaults.unitDefault)}</Pill>
            ) : on && hasAnyDefault(defaults) ? (
              <Pill tone="info">{C.pills.packageDefault}</Pill>
            ) : null}
            {on && !inBase ? <Pill>{C.pills.adding}</Pill> : null}
            {!on && inBase && autoAdded.has(id) && reqText ? (
              <Pill tone="warn" title={C.page.autoReAdd(reqText)}>
                {C.page.autoReAdd(reqText)}
              </Pill>
            ) : !on && inBase ? (
              <Pill tone="warn">{C.pills.removing}</Pill>
            ) : null}
            {unit ? (
              <span style={{ ...helpText, color: "var(--color-text-3)" }}>{unit}</span>
            ) : null}
            <span style={{ ...codeStyle, marginLeft: 0 }}>{r.node.code}</span>
          </>
        ),
      });
    }
    return { rows: out, bandIds };
  }, [
    metricById,
    base,
    draft,
    members,
    shown,
    baseIds,
    baseById,
    p.errorIds,
    autoAdded,
    partialTotals,
    collapsed,
    editable,
    filterIds,
    requiredBy,
    readOnlyReason,
    ctx,
  ]);

  const { rows, bandIds } = built;
  if (rows.length === 0) {
    return (
      <div role="status" style={{ ...helpText, margin: "12px 0" }} data-testid="package-tree-empty">
        <span>{C.filters.none}</span>
        {filterIds && p.onClearFilters ? (
          <button
            type="button"
            onClick={p.onClearFilters}
            style={{ ...linkStyle, marginLeft: 12 }}
            data-testid="package-tree-clear"
          >
            {C.filters.clear}
          </button>
        ) : null}
      </div>
    );
  }
  const anyClosed = bandIds.some((id) => collapsed.has(id));
  const tree = (
    <CheckTree
      rows={rows}
      ariaLabel={C.page.treeLabel}
      testId="package-members-tree"
      narrow={p.narrow === true}
      {...(p.fill ? { alwaysScroll: true, maxHeight: "100%" } : { maxHeight: 560 })}
      selectedId={p.selectedId}
      onSelect={p.onSelect}
      onCheck={(id) => {
        if (ctx.categories.has(id) && !shown.has(id)) p.onToggleCategory(id);
        else p.onToggle(id, !members.has(id));
      }}
      onToggleExpand={(id, open) => setBand(id, !open)}
      reasonVisible={false}
    />
  );
  const expandAll =
    bandIds.length > 0 ? (
      <div style={{ marginBottom: 4, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => toggleAll(bandIds)}
          style={linkStyle}
          data-testid="package-tree-expand-all"
        >
          {anyClosed ? C.tree.expandAll : C.tree.collapseAll}
        </button>
      </div>
    ) : null;
  const detail = p.detailBelow ? (
    <div
      data-testid="package-detail-below"
      style={{
        marginTop: 8,
        flexShrink: 0,
        maxHeight: p.fill ? 320 : undefined,
        overflowY: p.fill ? "auto" : undefined,
      }}
    >
      {p.detailBelow}
    </div>
  ) : null;
  return p.fill ? (
    <>
      {expandAll}
      <div style={{ position: "relative", flex: 1, minHeight: 200 }}>
        <div
          className="rounded-lg"
          style={{ position: "absolute", inset: 0, border: "1px solid var(--color-border)" }}
        >
          {tree}
        </div>
      </div>
      {detail}
    </>
  ) : (
    <>
      {expandAll}
      <div className="rounded-lg" style={{ border: "1px solid var(--color-border)" }}>
        {tree}
      </div>
      {detail}
    </>
  );
}
